// src/components/TripsCarousel.jsx
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    startAfter,
    limit
} from "firebase/firestore";
import { db } from "../firebase";

export default function TripsCarousel({
    collectionName = "trips",
    pageSize = 6,                // can be overridden by maxPages/defaultPerSlide
    orderByField = "startDate",
    orderDir = "desc",
    whereClause = ["visibility", "==", "public"],
    onSelect = () => { },
    defaultPerSlide = null,      // if provided, force X cards per slide
    maxPages = null,             // if provided, pageSize will be defaultPerSlide * maxPages (if pageSize wasn't set explicitly)
    items: itemsProp = null,     // NEW: optional items passed from parent (controlled mode)
}) {
    // If user provided maxPages and defaultPerSlide, compute pageSize automatically
    if (maxPages && defaultPerSlide && (!pageSize || pageSize === 6)) {
        pageSize = defaultPerSlide * maxPages;
    }

    // controlled mode if itemsProp provided
    const controlled = Array.isArray(itemsProp);

    const [items, setItems] = useState(controlled ? itemsProp : []);
    const [lastDoc, setLastDoc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        if (controlled) {
            setItems(Array.isArray(itemsProp) ? itemsProp : []);
            setLoading(false);
            // if items provided is less than requested pageSize we can assume there's no more
            setHasMore(Array.isArray(itemsProp) ? (itemsProp.length >= pageSize) : true);
            return;
        }
        // existing behaviour for non-controlled
        setItems([]);
    }, [itemsProp, controlled, pageSize]);


    const getPerSlide = () => {
        // If caller explicitly set defaultPerSlide, use it (don't auto-resize)
        if (typeof defaultPerSlide === "number" && defaultPerSlide > 0) return defaultPerSlide;

        const w = window.innerWidth;
        if (w < 640) return 1;
        if (w < 960) return 2;
        return 3;
    };
    const [perSlide, setPerSlide] = useState(getPerSlide());
    useEffect(() => {
        const onResize = () => setPerSlide(getPerSlide());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const totalSlides = Math.max(1, Math.ceil(items.length / perSlide));

    const fetchPage = async () => {
        // if parent passed items, do not attempt to fetch from Firestore
        if (controlled) {
            // ensure we are not left in a loading state
            if (mountedRef.current) {
                setLoading(false);
                // if itemsProp length < pageSize assume there's no more pages to fetch
                setHasMore(Array.isArray(itemsProp) && itemsProp.length >= pageSize);
            }
            return null;
        }

        if (!hasMore || loading) return null;
        setLoading(true);
        setError(null);

        try {
            // defensive: ensure whereClause is valid
            if (!Array.isArray(whereClause) || whereClause.length < 3) {
                throw new Error("Invalid whereClause passed to TripsCarousel: " + JSON.stringify(whereClause));
            }

            const colRef = collection(db, collectionName);

            // build query
            let q;
            if (lastDoc) {
                q = query(
                    colRef,
                    where(...whereClause),
                    orderBy(orderByField, orderDir),
                    startAfter(lastDoc),
                    limit(pageSize)
                );
            } else {
                q = query(
                    colRef,
                    where(...whereClause),
                    orderBy(orderByField, orderDir),
                    limit(pageSize)
                );
            }

            console.debug("TripsCarousel: running query", { collectionName, whereClause, orderByField, orderDir, pageSize, lastDocExists: !!lastDoc });

            const snap = await getDocs(q);

            if (!snap || !Array.isArray(snap.docs)) {
                // unexpected shape
                console.warn("TripsCarousel: getDocs returned unexpected snapshot:", snap);
                setHasMore(false);
                return null;
            }

            if (snap.empty) {
                // no documents at all
                console.debug("TripsCarousel: query returned 0 documents (snap.empty).");
                if (mountedRef.current) {
                    setHasMore(false);
                    setLastDoc(null);
                }
                return [];
            }

            const docs = snap.docs;
            const newItems = docs.map((d) => {
                const data = typeof d.data === "function" ? d.data() : d.data || {};
                return {
                    id: d.id,
                    slug: data.slug || data.slugified || null,
                    title: data.title || data.name || data.tripName || "Untitled",
                    excerpt: data.excerpt || data.summary || data.description || "",
                    tags: Array.isArray(data.tags) ? data.tags : (data.tags ? [data.tags] : []),
                    startDate: data.startDate || data.date || data.start || null,
                    raw: data,
                    _docRef: d, // keep doc reference for pagination
                };
            });

            if (mountedRef.current) {
                setItems((prev) => {
                    // avoid accidental duplicates (by id)
                    const ids = new Set(prev.map(x => x.id));
                    const merged = [...prev];
                    for (const it of newItems) {
                        if (!ids.has(it.id)) {
                            merged.push(it);
                            ids.add(it.id);
                        }
                    }
                    return merged;
                });

                // set lastDoc to last returned firestore doc (for pagination)
                setLastDoc(docs.length > 0 ? docs[docs.length - 1] : lastDoc);

                // If fewer docs than pageSize were returned, there is no more
                if (docs.length < pageSize) {
                    setHasMore(false);
                } else {
                    // docs.length === pageSize -> possibly more available
                    setHasMore(true);
                }
            }

            return newItems;
        } catch (err) {
            console.error("TripsCarousel fetchPage error:", err);
            if (mountedRef.current) {
                setError(err && err.message ? err.message : String(err));
                // If the error indicates missing index or permission, turn off hasMore to stop repeated failing calls
                setHasMore(false);
            }
            return null;
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    };

    useEffect(() => { fetchPage(); /* eslint-disable-next-line */ }, []);

    useEffect(() => {
        if (currentSlide >= totalSlides - 1 && hasMore && !loading && !controlled) {
            const t = setTimeout(() => fetchPage(), 200);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [currentSlide, totalSlides, hasMore, loading, controlled]); // eslint-disable-line

    const goPrev = () => setCurrentSlide((s) => Math.max(0, s - 1));
    const goNext = () => setCurrentSlide((s) => Math.min(totalSlides - 1, s + 1));

    const startIndex = currentSlide * perSlide;
    const visible = items.slice(startIndex, startIndex + perSlide);

    return (
        <div className="carousel-wrap" style={{ width: "100%", maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                {/* left: page indicator */}
                <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ fontSize: 13, color: "#6b7280", marginRight: 8 }}>{currentSlide + 1}/{totalSlides}</div>
                </div>

                {/* right: navigation arrows (float visually to the right) */}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                        aria-label="Previous"
                        onClick={goPrev}
                        disabled={currentSlide === 0}
                        className="btn-ghost"
                        style={{ opacity: currentSlide === 0 ? 0.45 : 1 }}
                    >
                        ‹
                    </button>

                    <button
                        aria-label="Next"
                        onClick={goNext}
                        disabled={!hasMore && currentSlide >= totalSlides - 1}
                        className="btn-ghost"
                        style={{ opacity: (!hasMore && currentSlide >= totalSlides - 1) ? 0.45 : 1 }}
                    >
                        ›
                    </button>
                </div>
            </div>

            <div className="carousel-track" style={{ display: "grid", gridTemplateColumns: `repeat(${perSlide}, 1fr)`, gap: 12 }}>
                {error && (
                    <div style={{ color: "crimson", gridColumn: `1 / span ${perSlide}` }}>
                        Error loading community trips: {error}
                        <div style={{ fontSize: 12, color: "#666" }}>Check console for full stacktrace.</div>
                    </div>
                )}

                {visible.length === 0 && !loading && !error && (
                    <div style={{ color: "#6b7280" }}>No trips yet.</div>
                )}

                {visible.map((r) => (
                    <div key={r.id || r.slug || r.title} className="result-card" role="button" tabIndex={0} onClick={() => onSelect(r)} onKeyDown={(e) => { if (e.key === "Enter") onSelect(r); }} style={{ cursor: "pointer" }}>
                        <strong style={{ display: "block" }}>{r.title}</strong>
                        <div style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}>{r.excerpt ? (r.excerpt.length > 120 ? r.excerpt.slice(0, 120) + "…" : r.excerpt) : "No description"}</div>
                        {Array.isArray(r.tags) && r.tags.length > 0 && (
                            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {r.tags.slice(0, 3).map((t) => (<div key={t} className="tag-pill" style={{ padding: "4px 8px", fontSize: 12 }}>#{t}</div>))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>{loading && items.length === 0 ? <span style={{ color: "#6b7280" }}>Loading…</span> : null}</div>
            </div>
        </div>
    );
}

TripsCarousel.propTypes = {
    collectionName: PropTypes.string,
    pageSize: PropTypes.number,
    orderByField: PropTypes.string,
    orderDir: PropTypes.oneOf(["asc", "desc"]),
    whereClause: PropTypes.array,
    onSelect: PropTypes.func,
    defaultPerSlide: PropTypes.number,
    maxPages: PropTypes.number,
    items: PropTypes.array,
};
